import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';

import { Permission } from '__generated__/graphql';
import { gql } from '__generated__';

import { EditUserPermissionsForm } from './EditPermissionsForm';
import { GetSessionQuery, useSession } from '../Auth/SessionProvider';
import {
  getServerErrors, isGraphQLAuthenticationError, isGraphQLAuthenticatorRequiredError,
} from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import GetUsersForProjectQuery from './GetUsersForProjectQuery';
import UI from '../../common/UI';
import useAuthenticatorVerificationModal from '../../common/useAuthenticatorVerificationModal';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';
import useValidation from '../../common/useValidation';

const editUserRoleSchema = yup.object().shape({
  permissions: yup.array().min(1).required(),
});

export const EditUserRoleMutation = gql(`
  mutation EditUserRole($input: EditUserRoleInput!) {
    editUserRole(input: $input) {
      id
    }
  }
`);

interface User {
  id: string;
  email: string;
}

export interface EditUserRoleModalProps {
  user: User;
  permissions: Permission[];
  onSuccess?: () => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

export const EditUserRoleModal = ({
  user, permissions, onSuccess, onCancel,
  refetchQueries = [GetSessionQuery, GetUsersForProjectQuery],
}: EditUserRoleModalProps,
) => {
  const { t } = useTranslation();
  const { user: currentUser } = useSession();
  const history = useHistory();
  const notifier = useNotifier();
  const project = useProject();

  const form = useForm({
    values: {
      permissions,
    },
  });

  const changed = !isEqual(form.values.permissions.slice().sort(), permissions.slice().sort());

  const [showAuthenticatorVerificationModal] = useAuthenticatorVerificationModal({
    onSuccess: () => editUser(),
  });

  const [editUser, { loading, error }] = useMutation(
    EditUserRoleMutation, {
      variables: {
        input: {
          project_id: project.id,
          user_id: user.id,
          ...form.values,
        },
      },
      onCompleted: () => {
        notifier.success(t('notifications.user_saved'));
        onSuccess?.();
      },
      onError: (error) => {
        if (isGraphQLAuthenticatorRequiredError(error)) {
          showAuthenticatorVerificationModal();
        } else if (isGraphQLAuthenticationError(error)) {
          // Redirect away if the authenticated user is no longer an admin.
          history.push(`/${project.id}`);
        }
      },
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const { valid, errors } = useValidation({
    schema: editUserRoleSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editUser()}>
      <UI.FormGrid>
        <UI.H4>
          {`${t('edit_user')}: ${user.email}`}
        </UI.H4>

        <UI.HR />

        <EditUserPermissionsForm form={form} errors={errors} />

        {changed && currentUser.id === user.id && (
          <UI.Warning>
            {t('edit_own_user_warning')}
          </UI.Warning>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save')}
          onClose={onCancel}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseEditUserRoleModalProps {
  user: User;
  permissions: Permission[];
}

const useEditUserRoleModal = ({ user, permissions }: UseEditUserRoleModalProps) => {
  const [openModal, closeModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeModal}>
      <EditUserRoleModal
        user={user}
        permissions={permissions}
        onSuccess={closeModal}
        onCancel={closeModal}
      />
    </UI.Modal>
  ), [user, permissions]);

  return [openModal, closeModal];
};

export default useEditUserRoleModal;
